import { type IconName } from '@dev-spendesk/grapes';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';

import { type SaveAllChanges } from './useAnalyticalFieldMapping';
import { useFieldTypeDescription } from './useFieldTypeDescription';
import {
  type AnalyticalFieldValueMappingV2,
  type AnalyticalFieldMappingFieldType,
  type AnalyticalFieldMappingV2,
  type InternalValue,
} from '../../bookkeep';
import { useAccountingSoftwareName } from '../apis';
import { type AnalyticalFieldExportSettings } from '../apis/useAnalyticalFieldExportSettings';
import { type SyncMode } from '../apis/useSetSyncSettings';

type Mode = 'settings' | 'remap';

type Props<TField extends InternalValue, TValue extends InternalValue> = {
  internalField: TField;
  fieldType: AnalyticalFieldMappingFieldType;
  exportConfig: AnalyticalFieldExportSettings[];
  isExportConfigEnabled: boolean;
  analyticalFieldMappings: AnalyticalFieldMappingV2[];
  syncMode: SyncMode | undefined;
  onClose: () => void;
  onSaveChanges: SaveAllChanges;
  internalValues: TValue[];
};

type ErrorType = {
  selectedField: boolean;
  exportSettings: boolean;
};

export const useAnalyticalFieldMappingMaintenanceModal = <
  TField extends InternalValue,
  TValue extends InternalValue,
>({
  fieldType,
  analyticalFieldMappings,
  internalField,
  syncMode,
  isExportConfigEnabled,
  onClose,
  onSaveChanges,
  internalValues,
  exportConfig,
}: Props<TField, TValue>) => {
  const { t } = useTranslation('global');
  const { accountingSoftwareName } = useAccountingSoftwareName();
  const [selectedSyncMode, setSelectedSyncMode] = useState<SyncMode>();
  const [fieldMapping, setFieldMapping] = useState<AnalyticalFieldMappingV2>();
  const [selectedExportSettings, setSelectedExportSettings] =
    useState<AnalyticalFieldExportSettings>({
      externalId: '',
      sendOnHeader: false,
      sendOnLine: true,
    });
  const [mappingInDatabase, setMappingInDatabase] =
    useState<AnalyticalFieldMappingV2>();

  const [mode, setMode] = useState<Mode>('settings');
  const [errors, setErrors] = useState<ErrorType>({
    selectedField: false,
    exportSettings: false,
  });
  const externalIdReference = useRef<string>(null);
  const fieldTypeDescription = useFieldTypeDescription(fieldType);

  const [fieldMappingValues, setFieldMappingValues] = useState<
    AnalyticalFieldValueMappingV2[]
  >([]);

  const handleSyncModeChanged = (value: SyncMode) => {
    setSelectedSyncMode(value);
  };

  const handleFieldMappingSelected = (
    field: AnalyticalFieldMappingV2 | undefined,
  ) => {
    setErrors((previous) => ({
      ...previous,
      selectedField: false,
    }));

    setFieldMapping(field);
    setFieldMappingValues(field?.values ?? []);
  };

  useEffect(() => {
    const currentFieldMapping = analyticalFieldMappings.find(
      ({ internalId }) => internalId === internalField.id,
    );

    setMappingInDatabase(currentFieldMapping);

    setSelectedExportSettings(
      exportConfig.find(
        ({ externalId }) => externalId === currentFieldMapping?.externalId,
      ) ?? {
        externalId: '',
        sendOnHeader: false,
        sendOnLine: true,
      },
    );
  }, [analyticalFieldMappings, internalField]);

  useEffect(() => {
    if (fieldMapping) {
      externalIdReference.current = fieldMapping.externalId;
    }
  }, [fieldMapping]);

  const handleExportSettingsChanged = useCallback(
    (settings: AnalyticalFieldExportSettings) => {
      setErrors((previous) => ({
        ...previous,
        exportSettings: false,
      }));

      if (externalIdReference.current) {
        setSelectedExportSettings({
          ...settings,
          externalId: externalIdReference.current,
        });
      }
    },
    [fieldMapping],
  );

  // Case where the button needs to say "Map Values"
  // basically if currently we have sync mode none,
  // and we need have some mapped values
  const isThisFromNoSyncToSomeSync = (): boolean => {
    return !!(
      syncMode === 'none' && // initial
      selectedSyncMode !== 'none' &&
      internalValues.length &&
      fieldMapping &&
      fieldMapping.values.some(({ internalId }) => !internalId) &&
      mode !== 'remap'
    );
  };

  // Use the same function to handle showing `map` button
  // and to actually redirect to mapping screen
  const shouldShowMapping = (): boolean => {
    // if we want to choose no sync, no need to map values
    if (selectedSyncMode === 'none') {
      return false;
    }

    // handle the no sync case first
    if (isThisFromNoSyncToSomeSync()) {
      return true;
    }

    // even if value is mapped to something,
    // if it is not mapped to the current spendesk field,
    //  it should be treated as not mapped
    const valuesSet = new Set(internalValues.map(({ id }) => id));
    const newFieldUnmappedValues = fieldMapping?.values.some(
      (mappingValue) =>
        !mappingValue.internalId || !valuesSet.has(mappingValue.internalId),
    );

    return !!(
      mappingInDatabase && // field is currently mapped in db
      mappingInDatabase.id !== fieldMapping?.id && // newly selected field is different than currently mapped
      internalValues.length && // the field has at least one value
      newFieldUnmappedValues && // if there are unmapped values for the newly selected field
      mode !== 'remap'
    );
  };

  const isValid = useCallback(() => {
    const selectedField = selectedSyncMode !== 'none' && !fieldMapping;
    const exportSettings =
      !selectedExportSettings ||
      (!selectedExportSettings.sendOnHeader &&
        !selectedExportSettings.sendOnLine);

    const valid =
      !selectedField && (isExportConfigEnabled ? !exportSettings : true);

    setErrors({
      selectedField,
      exportSettings: isExportConfigEnabled ? exportSettings : false,
    });

    return valid;
  }, [fieldMapping, selectedSyncMode, selectedExportSettings]);

  const handleUpdateSettings = useCallback(() => {
    if (shouldShowMapping()) {
      setMode('remap');
      return;
    }

    if (!isValid()) {
      return;
    }

    onSaveChanges({
      previousMapping: mappingInDatabase,
      mapping: fieldMapping
        ? {
            fieldId: fieldMapping.id,
            fieldType: selectedSyncMode === 'none' ? null : fieldType,
            internalId: selectedSyncMode === 'none' ? null : internalField.id,
            values:
              selectedSyncMode === 'none'
                ? fieldMappingValues.map((value) => ({
                    ...value,
                    internalId: null,
                  }))
                : fieldMappingValues,
          }
        : undefined,
      syncMode: selectedSyncMode,
      exportSettings: selectedExportSettings,
    });

    handleClose();
  }, [
    fieldMapping,
    fieldMappingValues,
    selectedExportSettings,
    mode,
    selectedSyncMode,
  ]);

  const getFieldMapping = useCallback(
    (fieldInternalId: string | undefined) => {
      return analyticalFieldMappings.find(
        ({ internalId }) => internalId === fieldInternalId,
      );
    },
    [analyticalFieldMappings],
  );

  useEffect(() => {
    if (syncMode) {
      setSelectedSyncMode(syncMode);
    }
  }, [syncMode]);

  useEffect(() => {
    setFieldMapping(getFieldMapping(internalField.id));
  }, [analyticalFieldMappings]);

  const handleBack = () => {
    if (mode === 'remap') {
      setMode('settings');
      return;
    }

    handleClose();
  };

  const handleClose = () => {
    if (selectedSyncMode !== syncMode) {
      setSelectedSyncMode(syncMode);
    }

    setErrors({
      selectedField: false,
      exportSettings: false,
    });
    setMode('settings');
    onClose();
  };

  const nextButton = useMemo((): { text: string; iconName?: IconName } => {
    // Case where the button needs to say "Map Values"
    // basically if currently we have sync mode none,
    // and we need have some mapped values
    if (isThisFromNoSyncToSomeSync()) {
      return {
        text: t(
          'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.mapValues',
        ),
        iconName: 'arrow-right',
      };
    }

    if (shouldShowMapping()) {
      return {
        text: t(
          'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.remapValues',
        ),
        iconName: 'arrow-right',
      };
    }

    return {
      text: t('misc.updateSettings'),
    };
  }, [fieldMapping, syncMode, selectedSyncMode, mode, internalValues]);

  const backButton = useMemo((): { text: string; iconName?: IconName } => {
    if (mode === 'remap') {
      return {
        text: t('misc.back'),
        iconName: 'arrow-left',
      };
    }

    return {
      text: t('misc.cancel'),
    };
  }, [mode]);

  const mappingInDatabaseCalloutWarningPoints = [
    t(
      'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.fieldMappingSwitchWarning.line1',
      {
        integration: accountingSoftwareName,
        fieldTypeDescription,
        previousFieldName: mappingInDatabase?.externalName,
        fieldName: fieldMapping?.externalName,
      },
    ),
    t(
      'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.fieldMappingSwitchWarning.line2',
    ),
  ];

  return {
    fieldMapping,
    nextButton,
    backButton,
    handleBack,
    handleClose,
    handleUpdateSettings,
    handleFieldMappingSelected,
    handleSyncModeChanged,
    handleExportSettingsChanged,
    setFieldMapping,
    mappingInDatabase,
    selectedSyncMode,
    selectedExportSettings,
    fieldTypeDescription,
    mappingInDatabaseCalloutWarningPoints,
    errors,
    mode,
    title:
      mode === 'remap'
        ? t(
            'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.maintenanceModeRemapTitle',
            {
              integration: accountingSoftwareName,
              fieldTypeDescription: internalField.name,
            },
          )
        : t(
            'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.maintenanceModeTitle',
            {
              integration: accountingSoftwareName,
              fieldTypeDescription: internalField.name,
            },
          ),
    fieldMappingValues,
    setFieldMappingValues,
  };
};
